// Builds and reads the intake answers a member's baseline week is saved
// under, for a flow client that asks the week questions itself. The keys are
// named here, beside their registry, so a client hands over counts and gets
// answers back without naming a key; the baselines module maps the same keys
// into a baseline at finalize.

import { isCalendarDate } from '../check-ins/schema';
import { readAnswer } from '../programs/program-key';
import {
  USAGE_CAPTURE_KEY,
  hasCompatibleUsageCapture,
  usageCaptureProvenanceSchema,
  usageProvenance,
  type UsageCaptureContext,
} from './usage-config';

/** Days in a baseline week: day 1 is the day before the anchor, day 7 a week before it. */
export const BASELINE_WEEK_LENGTH = 7;

/** A week's counts in day order: index 0 is day 1. */
export type BaselineWeekCounts = readonly number[];

export interface ReportedBaselineWeek {
  /** The member-local `YYYY-MM-DD` the week counts back from. */
  anchorDate: string;
  counts: BaselineWeekCounts;
}

export type BaselineWeekAnswers = Record<string, string | number>;

function isDayCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function assertWeek(counts: BaselineWeekCounts): void {
  if (counts.length !== BASELINE_WEEK_LENGTH || !counts.every(isDayCount)) {
    throw new RangeError('A baseline week is seven non-negative whole numbers');
  }
}

function dayAnswers(prefix: 'day_' | 'typical_day_', counts: BaselineWeekCounts) {
  return Object.fromEntries(counts.map((count, index) => [`${prefix}${index + 1}`, count]));
}

/**
 * The answers for the week the member reported: all seven days, zeros
 * included (a zero is an answer, not a gap), and the anchor they count back
 * from.
 */
export function reportedWeekAnswers({
  anchorDate,
  counts,
}: ReportedBaselineWeek): BaselineWeekAnswers {
  assertWeek(counts);
  if (!isCalendarDate(anchorDate)) throw new RangeError('The week anchor is not a calendar date');
  return { ...dayAnswers('day_', counts), week_anchor_date: anchorDate };
}

/** The member said the reported week was typical. */
export function typicalWeekConfirmedAnswers(): BaselineWeekAnswers {
  return { week_typical: 'yes' };
}

/**
 * The member said the reported week was not typical and gave a usual one.
 * `counts[N - 1]` is the usual count on the weekday of day N.
 */
export function typicalWeekAnswers(counts: BaselineWeekCounts): BaselineWeekAnswers {
  assertWeek(counts);
  return { week_typical: 'no', ...dayAnswers('typical_day_', counts) };
}

/**
 * The reported week saved in `answers`, or null unless every day holds a
 * whole count and the anchor is a calendar date: a client that resumes on a
 * later question needs the week the member saw, and a partial one is not it.
 */
export function readReportedWeek(answers: Record<string, unknown>): ReportedBaselineWeek | null {
  const anchorDate = readAnswer(answers, 'week_anchor_date');
  if (typeof anchorDate !== 'string' || !isCalendarDate(anchorDate)) return null;
  const counts: number[] = [];
  for (let day = 1; day <= BASELINE_WEEK_LENGTH; day += 1) {
    const count = readAnswer(answers, `day_${day}`);
    if (!isDayCount(count)) return null;
    counts.push(count);
  }
  return { anchorDate, counts };
}

/** A day's yes/no answer: 1 used, 0 no use, undefined not answered. */
export type BinaryDayChoice = 0 | 1 | undefined;

/** Answers for a yes/no week, as saved: strings, numbers, nulls and the capture object. */
export type BinaryWeekAnswers = Record<string, unknown>;

type AuditWindow = 'reported' | 'typical';

function assertChoices(choices: readonly BinaryDayChoice[]): void {
  if (
    choices.length !== BASELINE_WEEK_LENGTH ||
    !choices.every((choice) => choice === undefined || choice === 0 || choice === 1)
  ) {
    throw new RangeError('A yes/no week is seven answers of 0, 1 or unanswered');
  }
}

function priorCapture(answers: Record<string, unknown>, program: UsageCaptureContext) {
  if (!hasCompatibleUsageCapture(answers, program)) return null;
  const parsed = usageCaptureProvenanceSchema.safeParse(readAnswer(answers, USAGE_CAPTURE_KEY));
  return parsed.success ? parsed.data : null;
}

/**
 * Which days were assumed No at a window's first submission, kept across
 * edits. A compatible capture that already holds a record keeps it; one that
 * was already submitted for this window (its numeric days are there) but has
 * no record leaves that window unknown, since its saved zeros cannot prove a
 * deliberate No; otherwise the days left unanswered now are the record.
 */
function defaultAudit(
  answers: Record<string, unknown>,
  program: UsageCaptureContext,
  window: AuditWindow,
  choices: readonly BinaryDayChoice[],
) {
  const prior = priorCapture(answers, program);
  const kept = prior?.initialDefaultedNoDays ?? {};
  const prefix = window === 'typical' ? 'typical_' : '';
  const alreadySubmitted =
    prior !== null &&
    choices.every(
      (_, index) => typeof readAnswer(answers, `${prefix}day_${index + 1}`) === 'number',
    );
  const unanswered = choices.flatMap((choice, index) => (choice === undefined ? [index + 1] : []));
  const captured = kept[window] ?? (alreadySubmitted ? undefined : unanswered);
  return { ...kept, ...(captured === undefined ? {} : { [window]: captured }) };
}

function binaryDayAnswers(
  prefix: '' | 'typical_',
  choices: readonly BinaryDayChoice[],
): Record<string, number | string> {
  return Object.fromEntries(
    choices.flatMap((choice, index): [string, number | string][] => {
      const value = choice ?? 0;
      return [
        [`${prefix}day_${index + 1}`, value],
        [`${prefix}binary_day_${index + 1}`, String(value)],
      ];
    }),
  );
}

/**
 * The answers to clear before a yes/no week is captured over a session whose
 * saved answers belong to another capture (a counter week, another program,
 * another anchor). The session merges answers, so anything left behind would
 * still be validated: every day answer of both windows and the typical choice
 * become null, and the old numeric days move to `usage_capture_previous`
 * with the capture they were saved under.
 */
function staleAnswerClear(answers: Record<string, unknown>): BinaryWeekAnswers {
  const cleared: BinaryWeekAnswers = { week_typical: null };
  const previous: Record<string, unknown> = {};
  for (const prefix of ['', 'typical_']) {
    for (let day = 1; day <= BASELINE_WEEK_LENGTH; day += 1) {
      const key = `${prefix}day_${day}`;
      const value = readAnswer(answers, key);
      if (value !== undefined && value !== null) previous[key] = value;
      cleared[key] = null;
      const binaryKey = `${prefix}binary_day_${day}`;
      const binaryValue = readAnswer(answers, binaryKey);
      if (binaryValue !== undefined && binaryValue !== null) cleared[binaryKey] = null;
    }
  }
  if (Object.keys(previous).length > 0) {
    const history = readAnswer(answers, 'usage_capture_previous');
    const kept: unknown[] = Array.isArray(history) ? history : [];
    cleared.usage_capture_previous = [
      ...kept,
      { ...previous, provenance: readAnswer(answers, USAGE_CAPTURE_KEY) ?? null },
    ];
  }
  return cleared;
}

/**
 * The answers for a yes/no reported week, in the shape the mobile app saves:
 * each day as a `'0'`/`'1'` choice and the matching number, the anchor, and
 * the capture provenance with the days assumed No. An unanswered day is saved
 * as No. `answers` is the session as it stands: over another capture, the
 * write also clears what that capture left behind.
 */
export function binaryReportedWeekAnswers({
  anchorDate,
  choices,
  program,
  answers,
}: {
  anchorDate: string;
  choices: readonly BinaryDayChoice[];
  program: UsageCaptureContext;
  answers: Record<string, unknown>;
}): BinaryWeekAnswers {
  assertChoices(choices);
  if (!isCalendarDate(anchorDate)) throw new RangeError('The week anchor is not a calendar date');
  const provenance = usageProvenance(program, anchorDate);
  if (provenance === null) throw new RangeError('The program has no yes/no capture');
  const sameCapture =
    hasCompatibleUsageCapture(answers, program) &&
    readAnswer(answers, 'week_anchor_date') === anchorDate;
  const audit = sameCapture
    ? defaultAudit(answers, program, 'reported', choices)
    : {
        reported: choices.flatMap((choice, index) => (choice === undefined ? [index + 1] : [])),
      };
  return {
    ...(sameCapture ? {} : staleAnswerClear(answers)),
    ...binaryDayAnswers('', choices),
    week_anchor_date: anchorDate,
    [USAGE_CAPTURE_KEY]: {
      ...provenance,
      ...(Object.keys(audit).length > 0 ? { initialDefaultedNoDays: audit } : {}),
    },
  };
}

/**
 * The answers for a yes/no usual week the member gave after saying last week
 * was not typical, `choices[N - 1]` on the weekday of day N. The saved
 * reported week's capture is kept, with the typical days assumed No added.
 */
export function binaryTypicalWeekAnswers({
  choices,
  program,
  answers,
}: {
  choices: readonly BinaryDayChoice[];
  program: UsageCaptureContext;
  answers: Record<string, unknown>;
}): BinaryWeekAnswers {
  assertChoices(choices);
  const prior = priorCapture(answers, program);
  if (prior === null) throw new RangeError('No yes/no week is saved for this program');
  const audit = defaultAudit(answers, program, 'typical', choices);
  return {
    week_typical: 'no',
    ...binaryDayAnswers('typical_', choices),
    [USAGE_CAPTURE_KEY]: {
      ...prior,
      ...(Object.keys(audit).length > 0 ? { initialDefaultedNoDays: audit } : {}),
    },
  };
}

/**
 * The answers for a counted reported week. Over a session that holds another
 * capture (a yes/no week, another program, another anchor), the counts are
 * written under this program's own capture and the other capture's answers
 * are cleared in the same write, since the session merges answers and the
 * leftovers would refuse the week at finalize. Over a session with no capture
 * at all this is `reportedWeekAnswers` unchanged.
 */
export function countedReportedWeekAnswers({
  anchorDate,
  counts,
  program,
  answers,
}: {
  anchorDate: string;
  counts: BaselineWeekCounts;
  program: UsageCaptureContext;
  answers: Record<string, unknown>;
}): BinaryWeekAnswers {
  const week = reportedWeekAnswers({ anchorDate, counts });
  const held = readAnswer(answers, USAGE_CAPTURE_KEY);
  const holdsBinaryDays = Array.from(
    { length: BASELINE_WEEK_LENGTH },
    (_, index) => index + 1,
  ).some((day) =>
    ['', 'typical_'].some((prefix) => {
      const value = readAnswer(answers, `${prefix}binary_day_${day}`);
      return value !== undefined && value !== null;
    }),
  );
  if ((held === undefined || held === null) && !holdsBinaryDays) return week;
  const provenance = usageProvenance(program, anchorDate);
  if (provenance === null) return week;
  const sameCapture =
    hasCompatibleUsageCapture(answers, program) &&
    readAnswer(answers, 'week_anchor_date') === anchorDate;
  return {
    ...(sameCapture ? {} : staleAnswerClear(answers)),
    ...week,
    [USAGE_CAPTURE_KEY]: provenance,
  };
}
